/**
 * @fileoverview Settings API client
 * Handles all BenutzerSettings-related API calls
 */

import apiClient, { formatApiError } from "./client";

/**
 * @typedef {Object} BenutzerSettingsResponseDto
 * @property {boolean} ErinnerungVerschluesselnGespeicherteEmpfaenger
 * @property {boolean} AutomatischVerschluesselnGespeicherteEmpfaenger
 * @property {boolean} ErinnerungVerschluesselnAlleEmpfaenger
 * @property {boolean} NichtNachPasswortFragenFuerGespeicherteEmpfaenger
 * @property {boolean} BestaetigungBeiAenderungVerschluesselterMail
 * @property {string|null} VorlageBetreff
 * @property {boolean} HasCustomTemplate
 * @property {boolean} BetreffUnverschluesseltBehalten
 * @property {boolean} MsgDateiEinbetten
 * @property {boolean} NurPdfDirektVerschluesseln
 * @property {boolean} EntschluesseltGesendetSpeichern
 * @property {boolean} PasswortOptionenGuiDeaktivieren
 * @property {string|null} OptionenGuiPasswortHash
 * @property {string|null} OptionenGuiPasswortSalt
 * @property {boolean} StartbildschirmAnzeigen
 * @property {boolean} PasswortmanagerAktiviert
 * @property {boolean} ErinnerungEinmalVorVerschluesseln
 */

/**
 * Get user settings by oid
 * @param {string} oid - User's Azure AD oid
 * @returns {Promise<{success: boolean, data?: BenutzerSettingsResponseDto, message: string}>}
 */
export async function getSettings(oid) {
  try {
    const response = await apiClient.get(`/api/benutzer-settings/${oid}`);

    if (response.data.success) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Einstellungen erfolgreich geladen",
      };
    }

    return {
      success: false,
      message: response.data.message || "Fehler beim Laden der Einstellungen",
    };
  } catch (error) {
    console.error("Error getting settings:", error);
    return {
      success: false,
      message: formatApiError(error, "Fehler beim Laden der Einstellungen"),
    };
  }
}

/**
 * Update user settings
 * @param {string} oid - User's Azure AD oid
 * @param {Object} settingsUpdate - Settings to update
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function updateSettings(oid, settingsUpdate) {
  try {
    const response = await apiClient.put(
      `/api/benutzer-settings/${oid}`,
      settingsUpdate
    );

    if (response.data.success) {
      return {
        success: true,
        message:
          response.data.message || "Einstellungen erfolgreich gespeichert",
      };
    }

    return {
      success: false,
      message:
        response.data.message || "Fehler beim Speichern der Einstellungen",
    };
  } catch (error) {
    console.error("Error updating settings:", error);
    return {
      success: false,
      message: formatApiError(error, "Fehler beim Speichern der Einstellungen"),
    };
  }
}

/**
 * Lock settings GUI with password
 * @param {string} oid - User's Azure AD oid
 * @param {string} password - Password to lock settings
 * @param {string} passwordConfirm - Password confirmation
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function lockSettings(oid, password, passwordConfirm) {
  try {
    const response = await apiClient.post(
      `/api/benutzer-settings/${oid}/sperren`,
      {
        password,
        passwordConfirm,
      }
    );

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || "Einstellungen erfolgreich gesperrt",
      };
    }

    return {
      success: false,
      message: response.data.message || "Fehler beim Sperren der Einstellungen",
    };
  } catch (error) {
    console.error("Error locking settings:", error);
    return {
      success: false,
      message: formatApiError(error, "Fehler beim Sperren der Einstellungen"),
    };
  }
}

/**
 * Unlock settings GUI with password
 * @param {string} oid - User's Azure AD oid
 * @param {string} password - Password to unlock settings
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function unlockSettings(oid, password) {
  try {
    const response = await apiClient.post(
      `/api/benutzer-settings/${oid}/freischalten`,
      {
        password,
      }
    );

    if (response.data.success) {
      return {
        success: true,
        message:
          response.data.message || "Einstellungen erfolgreich freigegeben",
      };
    }

    return {
      success: false,
      message:
        response.data.message || "Fehler beim Freigeben der Einstellungen",
    };
  } catch (error) {
    console.error("Error unlocking settings:", error);
    return {
      success: false,
      message: formatApiError(error, "Fehler beim Freigeben der Einstellungen"),
    };
  }
}

/**
 * Reset settings to default values (VorlageBodyHtml remains unchanged)
 * @param {string} oid - User's Azure AD oid
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function resetSettings(oid) {
  try {
    const response = await apiClient.delete(
      `/api/benutzer-settings/${oid}/reset`
    );

    if (response.data.success) {
      return {
        success: true,
        message:
          response.data.message || "Einstellungen erfolgreich zurückgesetzt",
      };
    }

    return {
      success: false,
      message:
        response.data.message || "Fehler beim Zurücksetzen der Einstellungen",
    };
  } catch (error) {
    console.error("Error resetting settings:", error);
    return {
      success: false,
      message: formatApiError(error, "Fehler beim Zurücksetzen der Einstellungen"),
    };
  }
}

/**
 * Save custom email template (VorlageBodyHtml)
 * @param {string} oid - User's Azure AD oid
 * @param {string} htmlContent - HTML content to save as template
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function saveCustomTemplate(oid, htmlContent) {
  try {
    const response = await apiClient.post(
      `/api/benutzer-settings/${oid}/vorlageBody`,
      {
        html: htmlContent,
      }
    );

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || "Vorlage erfolgreich gespeichert",
      };
    }

    return {
      success: false,
      message: response.data.message || "Fehler beim Speichern der Vorlage",
    };
  } catch (error) {
    console.error("Error saving custom template:", error);
    return {
      success: false,
      message: formatApiError(error, "Fehler beim Speichern der Vorlage"),
    };
  }
}

/**
 * Reset custom email template to default
 * @param {string} oid - User's Azure AD oid
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function resetCustomTemplate(oid) {
  try {
    const response = await apiClient.delete(
      `/api/benutzer-settings/${oid}/vorlageBody`
    );

    if (response.data.success) {
      return {
        success: true,
        message: response.data.message || "Vorlage erfolgreich zurückgesetzt",
      };
    }

    return {
      success: false,
      message: response.data.message || "Fehler beim Zurücksetzen der Vorlage",
    };
  } catch (error) {
    console.error("Error resetting custom template:", error);
    return {
      success: false,
      message: formatApiError(error, "Fehler beim Zurücksetzen der Vorlage"),
    };
  }
}
